import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  type CompTopicDetailBucket,
  type OverviewCompetitorReportRow,
  type OverviewReportRow,
} from "@nexus/contracts";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useDashboardFiltersStore } from "@/features/dashboard/stores/dashboardFilters.store";
import type { CompetitorFiltersInput, MyBrandFiltersInput } from "@/lib/queries/dashboard.queries";
import {
  overviewSummaryQueryOptions,
  overviewCompetitorSummaryQueryOptions,
  overviewStatusQueryOptions,
  pickOverviewRequestWindow,
  MAX_POLL_DURATION_MS,
  type CompetitorOverviewFilters,
  type MyBrandOverviewFilters,
  type OverviewRow,
} from "@/lib/queries/overview.queries";
import { tenantSettingsQueryOptions } from "@/lib/queries/tenant-settings.queries";
import {
  COMPETITOR_EFFECTIVE_STATUS_OPTIONS,
  MY_BRAND_EFFECTIVE_STATUS_OPTIONS,
} from "@/lib/ad-status";
import { useOverviewFiltersStore } from "../stores/overviewFilters.store.js";
import { CompetitorComparisonChart } from "./CompetitorComparisonChart/CompetitorComparisonChart.js";
import { CompetitorComparisonChartSkeleton } from "./CompetitorComparisonChart/CompetitorComparisonChartSkeleton.js";
import { TopConceptsOvertimeChart } from "./TopConceptsOvertimeChart/TopConceptsOvertimeChart.js";
import { TopConceptsOvertimeChartSkeleton } from "./TopConceptsOvertimeChart/TopConceptsOvertimeChartSkeleton.js";
import { OverviewChart } from "./OverviewChart/OverviewChart.js";
import { OverviewChartSkeleton } from "./OverviewChart/OverviewChartSkeleton.js";
import { TopicDetailDrawer, type TopicDetailTab } from "./TopicDetailDrawer/TopicDetailDrawer.js";
import {
  CompTopicDetailDrawer,
  type CompTopicDetailTab,
} from "./CompTopicDetailDrawer/CompTopicDetailDrawer.js";
import { resolveTopicDetailContext } from "../lib/topicDetailContext.js";
import { resolveCompTopicDetailContext, resolveOverviewSeriesLabels } from "../lib/compTopicDetailContext.js";

export function OverviewPage() {
  const { t } = useTranslation();
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const mode = useDashboardFiltersStore((s) => s.mode);
  const appliedMyBrandFilters = useDashboardFiltersStore((s) => s.appliedMyBrandFilters);
  const appliedCompetitorFilters = useDashboardFiltersStore((s) => s.appliedCompetitorFilters);

  // Only the APPLIED overview filters drive the query — draft edits wait for the global
  // "Apply changes" button (see ExploreFilterHeader). `applied` re-references only on apply/discard/reset.
  const applied = useOverviewFiltersStore((s) => s.applied);

  const { groupBy, groupByValues, subFeature, featureTimeFrame, objectType, effectiveStatus, myBrandEffectiveStatus, audienceSegment, scenario, performanceMetric } =
    applied;
  const chartViewMode = useOverviewFiltersStore((s) => s.chartViewMode);
  const competitorChartSortBucket = useOverviewFiltersStore((s) => s.competitorChartSortBucket);
  const competitorChartSortDirection = useOverviewFiltersStore((s) => s.competitorChartSortDirection);
  const toggleCompetitorChartSort = useOverviewFiltersStore((s) => s.toggleCompetitorChartSort);

  // Each mode keeps its own status vocabulary (see `@/lib/ad-status`); only the active one
  // reaches the wire. A fully-selected set collapses to empty so it takes the same
  // `ARRAY_LENGTH(@effectiveStatus) = 0` bypass — and the same cache key — as "All".
  const wireEffectiveStatus = useMemo(() => {
    const selected = mode === "competitors" ? effectiveStatus : myBrandEffectiveStatus;
    const optionCount =
      mode === "competitors"
        ? COMPETITOR_EFFECTIVE_STATUS_OPTIONS.length
        : MY_BRAND_EFFECTIVE_STATUS_OPTIONS.length;
    return selected.length >= optionCount ? [] : selected;
  }, [mode, effectiveStatus, myBrandEffectiveStatus]);

  const [topicDrawerOpen, setTopicDrawerOpen] = useState(false);
  const [topicDrawerTab, setTopicDrawerTab] = useState<TopicDetailTab>("info");
  const [selectedTopicContext, setSelectedTopicContext] = useState<ReturnType<typeof resolveTopicDetailContext>>(null);

  const [compTopicDrawerOpen, setCompTopicDrawerOpen] = useState(false);
  const [compTopicDrawerTab, setCompTopicDrawerTab] = useState<CompTopicDetailTab>("info");
  const [selectedCompTopicContext, setSelectedCompTopicContext] =
    useState<ReturnType<typeof resolveCompTopicDetailContext>>(null);

  const requestWindow = pickOverviewRequestWindow(
    mode,
    appliedMyBrandFilters,
    appliedCompetitorFilters,
  );

  // BQ-bound overview filters only (breakdown + performanceMetric excluded — frontend-only)
  const overviewFilters = useMemo(
    () => ({
      groupBy,
      groupByValues,
      subFeature,
      featureTimeFrame,
      objectType,
      effectiveStatus: wireEffectiveStatus,
      scenario,
      audienceSegment:
        mode === "competitors" ? [] : audienceSegment,
      startDate: requestWindow.startDate,
      endDate: requestWindow.endDate,
      advancedFilters: applied.advancedFilters,
    }),
    [mode, groupBy, groupByValues, subFeature, featureTimeFrame, objectType, wireEffectiveStatus, scenario, audienceSegment, applied.advancedFilters, requestWindow.startDate, requestWindow.endDate],
  );

  // Stable globalFilters for query key — appliedMyBrandFilters drives the cache hash
  const globalFilters = useMemo<MyBrandOverviewFilters>(
    () => ({
      platforms: appliedMyBrandFilters.platforms ?? [],
      accounts: appliedMyBrandFilters.accounts ?? [],
      products: appliedMyBrandFilters.products ?? [],
      dateRange: appliedMyBrandFilters.dateRange as MyBrandFiltersInput['dateRange'],
      startDate: appliedMyBrandFilters.startDate,
      endDate: appliedMyBrandFilters.endDate,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(appliedMyBrandFilters)],
  );

  const competitorFilters = useMemo<CompetitorOverviewFilters>(
    () => ({
      myIndustry: appliedCompetitorFilters.myIndustry,
      myBrand: appliedCompetitorFilters.myBrand,
      industryProducts: appliedCompetitorFilters.industryProducts,
      competitorsIndustry: appliedCompetitorFilters.competitorsIndustry,
      mainCompetitor: appliedCompetitorFilters.mainCompetitor,
      benchmark: appliedCompetitorFilters.benchmark,
      platforms: appliedCompetitorFilters.platforms,
      dateRange: appliedCompetitorFilters.dateRange as CompetitorFiltersInput['dateRange'],
      startDate: appliedCompetitorFilters.startDate,
      endDate: appliedCompetitorFilters.endDate,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(appliedCompetitorFilters)],
  );

  // Active query options switch on the dashboard mode.
  const summaryOptions = mode === "competitors"
    ? overviewCompetitorSummaryQueryOptions(activeTenantId, overviewFilters, competitorFilters)
    : overviewSummaryQueryOptions(activeTenantId, overviewFilters, globalFilters);

  // ─── Amber Path polling state ──────────────────────────────────────────────

  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const [pollStartedAt, setPollStartedAt] = useState(0);
  const [timedOut, setTimedOut] = useState(false);

  // Stable hash for effect-1 — keyed off the active query (mode + all filters),
  // so a mode toggle or filter change resets the poll just like before.
  const filtersHash = JSON.stringify(summaryOptions.queryKey);

  // Effect 1: reset poll state when filters change
  useEffect(() => {
    setCorrelationId(null);
    setPollStartedAt(0);
    setTimedOut(false);
  }, [filtersHash]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: summaryData } = useQuery(summaryOptions);

  // Effect 2: when summary returns a pending sentinel, start the poll clock
  useEffect(() => {
    if (summaryData?.status === "pending") {
      setCorrelationId(summaryData.correlationId);
      setPollStartedAt(Date.now());
      setTimedOut(false);
    }
  }, [summaryData]);

  const { data: statusData } = useQuery(
    overviewStatusQueryOptions(correlationId, pollStartedAt),
  );

  // Derive rows from whichever source settled — before Effect 3 so it can cancel the timeout
  const rows: OverviewRow[] | undefined =
    summaryData?.status === "ready"
      ? summaryData.rows
      : statusData?.status === "COMPLETED"
        ? statusData.rows
        : undefined;

  // Competitors comparison-chart data, from whichever source settled.
  const chartRows =
    summaryData?.status === "ready"
      ? summaryData.chartRows
      : statusData?.status === "COMPLETED"
        ? statusData.chartRows
        : undefined;

  // Weekly topic breakdown for the overtime chart, from whichever source settled.
  const weeklyRows =
    summaryData?.status === "ready"
      ? summaryData.weeklyRows
      : statusData?.status === "COMPLETED"
        ? statusData.weeklyRows
        : undefined;

  // My-Brand per-Scenario chart rows, from whichever source settled.
  const myBrandChartRows =
    summaryData?.status === "ready"
      ? summaryData.myBrandChartRows
      : statusData?.status === "COMPLETED"
        ? statusData.myBrandChartRows
        : undefined;

  const myBrandKpiSeries =
    summaryData?.status === "ready"
      ? summaryData.myBrandKpiSeries
      : statusData?.status === "COMPLETED"
        ? statusData.myBrandKpiSeries
        : undefined;

  const reportRows = useMemo(
    () => (rows ?? []).filter((r): r is OverviewReportRow => "SPEND" in r),
    [rows],
  );

  const compReportRows = useMemo(
    () => (rows ?? []).filter((r): r is OverviewCompetitorReportRow => "Examples" in r && !("SPEND" in r)),
    [rows],
  );

  const { data: tenantSettings } = useQuery(tenantSettingsQueryOptions(activeTenantId));
  const defaultBrand = tenantSettings?.defaultBrand ?? null;

  const seriesLabels = useMemo(
    () =>
      resolveOverviewSeriesLabels({
        myBrand: competitorFilters.myBrand,
        defaultBrand,
        mainCompetitor: competitorFilters.mainCompetitor,
        t,
      }),
    [competitorFilters.myBrand, competitorFilters.mainCompetitor, defaultBrand, t],
  );

  // Close drawers when applied filters, mode, chart view mode, or performance metric change.
  useEffect(() => {
    setTopicDrawerOpen(false);
    setSelectedTopicContext(null);
    setTopicDrawerTab("info");
    setCompTopicDrawerOpen(false);
    setSelectedCompTopicContext(null);
    setCompTopicDrawerTab("info");
  }, [filtersHash, mode, chartViewMode, performanceMetric]);

  const handleTopicSelect = useCallback(
    (topicTitle: string) => {
      if (!myBrandChartRows) return;
      const context = resolveTopicDetailContext({
        reportRows,
        chartRows: myBrandChartRows,
        topicTitle,
      });
      if (!context) return;
      setSelectedTopicContext(context);
      setTopicDrawerOpen(true);
    },
    [myBrandChartRows, reportRows],
  );

  const handleCompTopicBucketSelect = useCallback(
    (topicTitle: string, bucket: CompTopicDetailBucket) => {
      if (!chartRows) return;
      const context = resolveCompTopicDetailContext({
        reportRows: compReportRows,
        chartRows,
        topicTitle,
        bucket,
        competitorFilters,
        defaultBrand,
        mainCompetitorLabel: competitorFilters.mainCompetitor,
        t,
      });
      if (!context) return;
      setSelectedCompTopicContext(context);
      setCompTopicDrawerOpen(true);
    },
    [chartRows, compReportRows, competitorFilters, defaultBrand, t],
  );

  // Backend failure (Amber Path FAILED). The poll stops server-side on FAILED, so surface
  // the error immediately rather than waiting out the 5-minute timer below.
  const failed = statusData?.status === "FAILED";
  const failureMessage = failed
    ? statusData?.message ?? "Something went wrong generating this report."
    : null;

  // Effect 3: hard timeout — cleanup cancels the timer; guard on rows/failed to skip once
  // data arrives or the backend reports a failure (which has its own message).
  useEffect(() => {
    if (!pollStartedAt || rows !== undefined || failed) return;
    const remaining = MAX_POLL_DURATION_MS - (Date.now() - pollStartedAt);
    if (remaining <= 0) {
      setTimedOut(true);
      return;
    }
    const id = setTimeout(() => setTimedOut(true), remaining);
    return () => clearTimeout(id);
  }, [pollStartedAt, rows, failed]);

  if (!activeTenantId) return null;

  return (
    <div className="flex flex-1 flex-col gap-4 p-4">
      {rows === undefined && !timedOut && !failed && (
        <div className="flex flex-col gap-4">
          {mode === "my-brand" ? (
            <OverviewChartSkeleton />
          ) : (
            <>
              <CompetitorComparisonChartSkeleton />
              <TopConceptsOvertimeChartSkeleton />
            </>
          )}
        </div>
      )}
      {failed && (
        <p data-testid="overview-error" className="text-sm text-destructive">
          {failureMessage}
        </p>
      )}
      {timedOut && !failed && (
        <p className="text-sm text-destructive">Request timed out.</p>
      )}

      {mode === "my-brand" && myBrandChartRows !== undefined && (
        <div>
          <OverviewChart
            chartRows={myBrandChartRows}
            kpiSeries={myBrandKpiSeries ?? []}
            selectedTopic={topicDrawerOpen ? selectedTopicContext?.topicTitle : null}
            onTopicSelect={handleTopicSelect}
          />
        </div>
      )}

      {mode === "my-brand" && activeTenantId && (
        <TopicDetailDrawer
          tenantId={activeTenantId}
          context={selectedTopicContext}
          open={topicDrawerOpen}
          activeTab={topicDrawerTab}
          onOpenChange={setTopicDrawerOpen}
          onActiveTabChange={setTopicDrawerTab}
          kpiSeries={myBrandKpiSeries ?? []}
          overviewFilters={overviewFilters}
          globalFilters={globalFilters}
        />
      )}

      {mode === "competitors" && chartRows !== undefined && (
        <div>
          <CompetitorComparisonChart
            chartRows={chartRows}
            performanceMetric={performanceMetric}
            youLabel={seriesLabels.you}
            mainCompetitorLabel={seriesLabels.mainCompetitor}
            benchmarkLabel={seriesLabels.benchmark}
            sortBucket={competitorChartSortBucket}
            sortDirection={competitorChartSortDirection}
            onToggleSort={toggleCompetitorChartSort}
            onTopicBucketSelect={handleCompTopicBucketSelect}
          />
        </div>
      )}

      {mode === "competitors" && activeTenantId && (
        <CompTopicDetailDrawer
          tenantId={activeTenantId}
          context={selectedCompTopicContext}
          open={compTopicDrawerOpen}
          activeTab={compTopicDrawerTab}
          onOpenChange={setCompTopicDrawerOpen}
          onActiveTabChange={setCompTopicDrawerTab}
          overviewFilters={overviewFilters}
          competitorFilters={competitorFilters}
        />
      )}

      {mode === "competitors" && chartRows !== undefined && weeklyRows !== undefined && weeklyRows.length > 0 && (
        <div>
          <TopConceptsOvertimeChart weeklyRows={weeklyRows} bucket="you" />
        </div>
      )}
    </div>
  );
}
